import {
  IssueType,
  useCurrentAccount,
  useTogglePin,
  WorkflowQuestionType,
} from '@gripp/shared-logic';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity, View } from 'react-native';
import { Divider, Menu } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { Icon } from '../crop-circle/icons';
import { DeleteIssueModal } from './deleteIssueModal';
import { useIssueContext } from './issueContext';
import { IssueEditRoutineLoader } from './issueEditRoutineLoader';
import { FunctionId } from './issueFormatUtils';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type IssueMenuProps = {
  issue: any;
  hasReminder: boolean;
  onIssueDelete: () => void;
  onEditPress?: () => void;
  onPrint?: () => Promise<void>;
  /** Custom trigger icon. Defaults to the Settings gear. */
  anchor?: ReactNode;
  anchorAccessibilityLabel?: string;
};

export const IssueMenu = (props: IssueMenuProps) => {
  const { t } = useTranslation();
  const { account } = useCurrentAccount();

  const [menuVisible, setMenuVisible] = useState(false);
  const [menuFired, setMenuFired] = useState(0);
  const [isEditIssueVisible, setIsEditIssueVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const isDesktop = () => Platform.OS === 'web';
  const { onRoutineSelected } = useIssueContext()!;

  const { togglePin } = useTogglePin();

  if (!props.issue) {
    return null;
  }

  const openMenu = () => {
    setMenuVisible(true);
    setMenuFired(Date.now());
  };

  const closeMenu = () => {
    // Add fired check to combat paper menu bug:
    // https://github.com/callstack/react-native-paper/issues/3716
    if (Date.now() - menuFired > 400) {
      setMenuVisible(false);
    }
  };

  const handlePinUnpin = async () => {
    await togglePin(props.issue.id, !props.issue.isPinned);
    closeMenu();
  };

  const onIssueEditPressed = async () => {
    closeMenu();

    if (props.onEditPress) {
      props.onEditPress();
    }
  };

  const onRoutineEditCanceled = () => {
    setIsEditIssueVisible(false);
  };

  const onRoutineEditCompleted = () => {
    setIsEditIssueVisible(false);
  };

  if (account?.effectivePermissions?.includes('*.restricted')) {
    return null;
  }

  return (
    <View>
      <Menu
        // The following fix the following issue: https://github.com/callstack/react-native-paper/issues/4807
        key={String(menuVisible)}
        statusBarHeight={0}
        visible={menuVisible}
        onDismiss={closeMenu}
        contentStyle={[
          isDesktop()
            ? styles.desktopMenu
            : Platform.OS === 'ios'
            ? styles.iosMenu
            : styles.mobileMenu,
          styles.white,
        ]}
        anchor={
          <TouchableOpacity
            onPress={openMenu}
            accessibilityRole="button"
            accessibilityLabel={
              props.anchorAccessibilityLabel ?? t('issue.menu.options')
            }
            testID="activity-header-menu"
          >
            {props.anchor ?? (
              <Icon name="Settings" size={20} color={Colors.role.textStrong} />
            )}
          </TouchableOpacity>
        }
      >
        <Menu.Item
          leadingIcon={'pencil'}
          style={styles.white}
          onPress={() => {
            onIssueEditPressed();
          }}
          title={t('issue.menu.edit')}
        />
        <Menu.Item
          leadingIcon={props.issue.isPinned ? 'pin-off' : 'pin'}
          style={styles.white}
          title={
            props.issue.isPinned
              ? t('issue.menu.unpin')
              : t('issue.menu.pinToAsset')
          }
          onPress={handlePinUnpin}
        />
        {(props.issue.createdBy?.id !== FunctionId ||
          props.issue.type === IssueType.Alert) &&
          (() => {
            // Frontend guard: if issue is from a one-time-only routine and the user is not global admin, hide delete
            const isOneTime =
              props.issue?.type === WorkflowQuestionType.Routine &&
              props.issue?.routineVersion?.config?.oneTimeOnly;

            const isGlobalAdmin = account?.isGlobalAdmin?.();
            if (isOneTime && !isGlobalAdmin) return null;

            return (
              <>
                <Divider style={styles.divider} />
                <Menu.Item
                  style={styles.white}
                  titleStyle={styles.delete}
                  onPress={() => {
                    setIsDeleteModalVisible(true);
                    closeMenu();
                  }}
                  title={t('issue.menu.delete')}
                />
              </>
            );
          })()}
      </Menu>
      {props.issue.type === WorkflowQuestionType.Routine &&
        isEditIssueVisible && (
          <IssueEditRoutineLoader
            isEditRoutineLoader={isEditIssueVisible}
            issue={props.issue}
            currentTitle={props.issue?.title}
            onAlertWorkflowDetected={() => {
              setIsEditIssueVisible(false);
            }}
            onRoutineEdition={(props) => {
              setIsEditIssueVisible(false);
              onRoutineSelected({
                ...props,
                onCanceled: () => onRoutineEditCanceled(),
                onCompleted: () => onRoutineEditCompleted(),
              });
            }}
          />
        )}
      {isDeleteModalVisible && (
        <DeleteIssueModal
          visible={isDeleteModalVisible}
          hide={() => setIsDeleteModalVisible(false)}
          issueId={props.issue?.id}
          onDelete={props.onIssueDelete}
        />
      )}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  white: {
    backgroundColor: role.surface,
  },
  delete: {
    paddingLeft: 10,
    color: Colors.red,
  },
  iosMenu: {
    top: 0,
    right: 0,
  },
  mobileMenu: {
    top: 50,
    right: 0,
  },
  desktopMenu: {
    top: 30,
  },
  divider: {
    marginTop: 5,
    marginBottom: 5,
    marginLeft: 20,
    marginRight: 20,
  },
}));
